import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useInventoryData } from "../hooks/useInventoryData";
import { useInvoiceReport } from "../hooks/useInvoiceReport";
import { useApi } from "../hooks/useApi";
import { useColumnMapping } from "../hooks/useColumnMapping";
import FilterBar from "../components/filters/FilterBar";
import GroupedInventoryTable from "../components/tables/GroupedInventoryTable";
import EditModal from "../components/modals/EditModal";
import SiteDetailsModal from "../components/modals/SiteDetailsModal";
import Toast from "../components/ui/Toast";
import { SpinnerCustom } from "../components/ui/Spinner";
import Loading from "../components/ui/Loading";
import { INVENTORY_DISPLAY_FIELDS } from "../config/inventoryFields";
import {
  formatConcatenatedAddress,
  buildBillingAccountProviderMap,
  formatCurrency,
} from "../utils/helpers";
import {
  filterSnapshotRecords,
  groupSnapshotRecordsByAddress,
  sortSnapshotAddressGroups,
  sumSnapshotGroupsMRC,
} from "../utils/snapshotInventoryUtils";
import {
  canMutateTenantData,
  INVOICE_ACCOUNT_SEARCH_KEY,
  INVENTORY_ACCOUNT_SEARCH_KEY,
  OVERVIEW_INVENTORY_FILTERS_KEY,
} from "../config/constants";
import { apiService } from "../services/apiService";
import {
  APP_NAVIGATE_EVENT,
  getAppHistoryModal,
  getAppHistoryPage,
  pushAppHistory,
} from "../utils/appHistory";

const InventoryPage = ({
  userRole,
  tenant,
  tenantId,
  providers = [],
  categories = [],
  statuses = [],
  serviceTypes = [],
  billingAccounts = [],
  billingAccountProviderMap: billingAccountProviderMapFromBackend = new Map(),
  filterCombinations = [],
  billingAccountsByProvider = [],
  categoriesByProvider = [],
  serviceTypesByProvider = [],
  filterBarProviders,
  filterBarBillingAccounts,
  filterBarBillingAccountProviderMap,
  filterBarBillingAccountsByProvider,
  dropdownLoading = false,
  refreshDropdownValues,
  setHeaderOptionsContent,
  onNavigate,
}) => {
  // Prefill search / advanced filters before first fetch (Invoices / Overview).
  // Do not remove sessionStorage in the initializer — React Strict Mode may run it
  // twice and would wipe the key before state is committed.
  const [accountSearchFilters] = useState(() => {
    try {
      const next = {};
      const accountSearch = String(
        sessionStorage.getItem(INVENTORY_ACCOUNT_SEARCH_KEY) || "",
      ).trim();
      if (accountSearch) next.search = accountSearch;

      const overviewRaw = sessionStorage.getItem(OVERVIEW_INVENTORY_FILTERS_KEY);
      if (overviewRaw) {
        const overview = JSON.parse(overviewRaw);
        if (overview?.search != null && String(overview.search).trim()) {
          next.search = String(overview.search).trim();
        }
        if (
          overview?.contractEndsWithin != null &&
          String(overview.contractEndsWithin).trim() !== ""
        ) {
          next.contractEndsWithin = String(overview.contractEndsWithin).trim();
        }
      }
      return next;
    } catch {
      return {};
    }
  });

  // Use the actual tenant ID from authenticated user (passed as prop)
  const {
    inventoryData,
    columns,
    grandTotalMRC,
    pagination,
    filters,
    loading,
    error,
    updateFilters,
    changePage,
    changePageSize,
    refreshData,
    patchGroupedSiteStatus,
    sortConfig,
    updateSort,
  } = useInventoryData(tenantId, accountSearchFilters, true); // Enable grouped mode

  // Consume handoff key after mount (safe under Strict Mode)
  useEffect(() => {
    try {
      sessionStorage.removeItem(INVENTORY_ACCOUNT_SEARCH_KEY);
      sessionStorage.removeItem(OVERVIEW_INVENTORY_FILTERS_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  // Refresh data when component mounts (especially after merge)
  useEffect(() => {
    const hasRecentImport = localStorage.getItem("lastImportId");
    if (hasRecentImport) {
      console.log("Recent import detected, refreshing inventory data");
      refreshData().then(() => {
        // Refresh dropdown values after inventory data is refreshed
        if (refreshDropdownValues) {
          refreshDropdownValues();
        }
      });
      localStorage.removeItem("lastImportId");
    }
  }, [refreshData, refreshDropdownValues]);

  const {
    updateInventoryRecord,
    getInventoryRecord,
    truncateTables,
    getDeletePreviewCount,
    deleteFilteredInventory,
    deleteInventoryItem,
    exportInventoryRecords,
    getGroupedInventoryData,
    getInventoryData,
  } = useApi();
  const { columnMapping, fieldMappingLookup } = useColumnMapping(
    filters.provider,
    filters.category,
    tenantId,
  );

  const [editingItem, setEditingItem] = useState(null);
  /** True while we intentionally history.back() to dismiss the Edit history entry. */
  const closingEditViaHistoryRef = useRef(false);

  const closeEditingItem = useCallback(() => {
    setEditingItem(null);
    if (getAppHistoryModal() === "edit") {
      closingEditViaHistoryRef.current = true;
      window.history.back();
    }
  }, []);

  // Browser Back closes Edit modal; Forward into a stale edit entry is ignored.
  // In-app navigate dismisses Edit without history.back() (modal entry already cleared).
  useEffect(() => {
    const onPopState = (event) => {
      if (closingEditViaHistoryRef.current) {
        closingEditViaHistoryRef.current = false;
        return;
      }
      if (getAppHistoryModal(event.state) === "edit") return;
      setEditingItem(null);
    };
    const onAppNavigate = () => {
      closingEditViaHistoryRef.current = false;
      setEditingItem(null);
    };
    window.addEventListener("popstate", onPopState);
    window.addEventListener(APP_NAVIGATE_EVENT, onAppNavigate);
    return () => {
      window.removeEventListener("popstate", onPopState);
      window.removeEventListener(APP_NAVIGATE_EVENT, onAppNavigate);
    };
  }, []);

  const [truncating, setTruncating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [deletePreviewCount, setDeletePreviewCount] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deletingRecordId, setDeletingRecordId] = useState(null);
  const [showDeleteRecordConfirmation, setShowDeleteRecordConfirmation] =
    useState(false);
  const [deletingRecord, setDeletingRecord] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [toast, setToast] = useState({ message: null, type: "success" });
  const [siteDetailsOpen, setSiteDetailsOpen] = useState(false);
  const [siteDetailsAddressKey, setSiteDetailsAddressKey] = useState(null);
  /** When set (non-undefined), SiteDetailsModal loads site fields from these snapshot rows only */
  const [siteDetailsSnapshotItems, setSiteDetailsSnapshotItems] =
    useState(undefined);
  const [siteFilterOptions, setSiteFilterOptions] = useState(null);
  const [siteFilterOptionsLoading, setSiteFilterOptionsLoading] =
    useState(false);

  /** Current = live inventory; Historical = monthly snapshot via invoice-report API */
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [snapshotPage, setSnapshotPage] = useState(1);
  const [snapshotPageSize, setSnapshotPageSize] = useState(20);
  const [snapshotSortConfig, setSnapshotSortConfig] = useState({
    sortBy: "address",
    sortOrder: "asc",
  });

  const isLiveView =
    selectedYear === null && selectedMonth === null;

  const {
    data: invoiceReportData,
    loading: snapshotLoading,
    error: snapshotReportError,
  } = useInvoiceReport(
    tenantId,
    isLiveView ? null : selectedYear,
    isLiveView ? null : selectedMonth,
  );

  const snapshotRowsRaw = invoiceReportData?.snapshot_data;
  const snapshotRowsFiltered = useMemo(
    () =>
      filterSnapshotRecords(
        Array.isArray(snapshotRowsRaw) ? snapshotRowsRaw : [],
        filters,
      ),
    [snapshotRowsRaw, filters],
  );

  const snapshotGroupsSorted = useMemo(() => {
    const grouped = groupSnapshotRecordsByAddress(snapshotRowsFiltered);
    const order =
      snapshotSortConfig?.sortOrder === "desc" ? "desc" : "asc";
    return sortSnapshotAddressGroups(grouped, order);
  }, [snapshotRowsFiltered, snapshotSortConfig.sortOrder]);

  const snapshotPagination = useMemo(() => {
    const totalGroups = snapshotGroupsSorted.length;
    // Match live inventory: sites = groups with configured site details
    const siteCount = snapshotGroupsSorted.filter((g) => {
      if (g.needs_master) return false;
      const hasSiteName = String(g.site_name || "").trim() !== "";
      const hasSiteStatus =
        g.site_status != null && String(g.site_status).trim() !== "";
      return hasSiteName || hasSiteStatus;
    }).length;
    const totalRecords = snapshotRowsFiltered.length;
    const totalPages = Math.max(
      1,
      Math.ceil(totalGroups / snapshotPageSize) || 1,
    );
    const page = Math.min(snapshotPage, totalPages);
    const offset = (page - 1) * snapshotPageSize;
    const slice = snapshotGroupsSorted.slice(
      offset,
      offset + snapshotPageSize,
    );
    return {
      page,
      totalPages,
      totalGroups,
      siteCount,
      totalRecords,
      slice,
      pageSize: snapshotPageSize,
    };
  }, [
    snapshotGroupsSorted,
    snapshotRowsFiltered.length,
    snapshotPage,
    snapshotPageSize,
  ]);

  const snapshotGrandTotalMRC = useMemo(
    () => sumSnapshotGroupsMRC(snapshotGroupsSorted),
    [snapshotGroupsSorted],
  );

  const transformedSnapshotData = useMemo(() => {
    if (!snapshotPagination.slice.length) return [];
    return snapshotPagination.slice.map((group) => ({
      ...group,
      address: formatConcatenatedAddress({
        std_street_address: group.std_street_address,
        std_city: group.std_city,
        std_state: group.std_state,
        std_zip: group.std_zip,
        std_country: group.std_country,
      }),
    }));
  }, [snapshotPagination.slice]);

  const filterBarInventory = isLiveView
    ? inventoryData
    : snapshotGroupsSorted;

  const currentBillingPeriod = useMemo(() => {
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    const now = new Date(
      new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" })
    );
    return `${monthNames[now.getMonth()]} ${now.getFullYear()}`;
  }, []);

  const handleSnapshotSort = useCallback((sortBy, sortOrder) => {
    setSnapshotSortConfig({
      sortBy: sortBy || "address",
      sortOrder: sortOrder || "asc",
    });
    setSnapshotPage(1);
  }, []);

  const changeSnapshotPage = useCallback((page) => {
    setSnapshotPage(page);
  }, []);

  const changeSnapshotPageSize = useCallback((size) => {
    setSnapshotPageSize(size);
    setSnapshotPage(1);
  }, []);

  useEffect(() => {
    setSnapshotPage(1);
  }, [selectedYear, selectedMonth]);

  useEffect(() => {
    if (!isLiveView) setSnapshotPage(1);
  }, [isLiveView, filters]);

  useEffect(() => {
    if (!tenantId) {
      setSiteFilterOptions(null);
      return undefined;
    }
    let cancelled = false;
    setSiteFilterOptionsLoading(true);
    apiService
      .getSiteInventoryFilterOptions(tenantId)
      .then((res) => {
        if (!cancelled) {
          setSiteFilterOptions(res?.data ?? null);
        }
      })
      .catch((err) => {
        console.error("Failed to load site filter options:", err);
        if (!cancelled) {
          setSiteFilterOptions({
            regions: [
              "Central",
              "Mid-West",
              "North East",
              "North West",
              "South East",
              "South West",
              "West",
            ],
            siteClassifications: [
              "Data Center",
              "HQ",
              "ASR",
              "Tier 1",
              "Tier 2",
              "Tier 3",
              "Tier 4",
              "Other",
            ],
            generalManagers: [],
            sdWanApplianceNames: [],
            siteStatuses: ["Active", "Closed"],
          });
        }
      })
      .finally(() => {
        if (!cancelled) {
          setSiteFilterOptionsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  useEffect(() => {
    if (!setHeaderOptionsContent) return undefined;
    setHeaderOptionsContent(null);
    return () => setHeaderOptionsContent(null);
  }, [setHeaderOptionsContent]);

  const handleEditItem = (itemData) => {
    const actualItem = itemData?.current || itemData;

    const pushEditHistoryIfNeeded = () => {
      if (getAppHistoryModal() === "edit") return;
      pushAppHistory({
        page: getAppHistoryPage() || "inventory",
        modal: "edit",
      });
    };

    // Historical month: open same modal read-only from snapshot row (no live API fetch)
    if (!isLiveView) {
      pushEditHistoryIfNeeded();
      setEditingItem({
        current: { ...actualItem },
        original: { ...actualItem },
        isNew: false,
        isSnapshotViewOnly: true,
      });
      return;
    }

    const recordId = actualItem?.id;

    if (!recordId && recordId !== 0) {
      console.error("No ID found in item. Full item:", actualItem);
      alert(
        "⚠️ Cannot edit: Record ID is missing. Please check console for details.",
      );
      return;
    }

    // Open modal immediately with row data (no loading overlay)
    pushEditHistoryIfNeeded();
    setEditingItem({
      current: { ...actualItem },
      original: { ...actualItem },
      isNew: false,
      isSnapshotViewOnly: false,
    });

    // Fetch full record in background and update modal when ready
    getInventoryRecord(recordId, tenantId)
      .then((response) => {
        const fullRecord =
          response?.data?.record || response?.data || response || null;
        if (fullRecord?.id) {
          if (
            fullRecord.address_id === undefined &&
            actualItem?.address_id !== undefined
          ) {
            fullRecord.address_id = actualItem.address_id;
          }
          setEditingItem({
            current: fullRecord,
            original: { ...fullRecord },
            isNew: false,
            isSnapshotViewOnly: false,
          });
        }
      })
      .catch((error) => {
        console.error("Failed to fetch inventory record:", error);
        if (error.isNetworkError || error.message === "Unable to load the data") {
          setToast({ message: "Unable to load the data", type: "error" });
        }
        // Modal already shows row data; no need to close
      });
  };

  const handleDeleteItem = (itemData) => {
    const actualItem = itemData?.current || itemData;
    const recordId = actualItem?.id;

    if (!recordId && recordId !== 0) {
      console.error("No ID found in item. Full item:", actualItem);
      alert(
        "⚠️ Cannot delete: Record ID is missing. Please check console for details.",
      );
      return;
    }

    setRecordToDelete(actualItem);
    setDeletingRecordId(recordId);
    setShowDeleteRecordConfirmation(true);
  };

  /**
   * @param {boolean} withDeleteSnapshot - true: save Azure delete snapshot (overwrite circuit); false: delete DB row only
   */
  const handleConfirmDeleteRecord = async (withDeleteSnapshot) => {
    if (!deletingRecordId) return;

    try {
      setDeletingRecord(true);
      await deleteInventoryItem(deletingRecordId, {
        version: recordToDelete?.version,
        skip_delete_snapshot: !withDeleteSnapshot,
        preserve_existing_delete_snapshot: false,
      });
      await refreshData();
      if (refreshDropdownValues) {
        refreshDropdownValues();
      }
      setShowDeleteRecordConfirmation(false);
      setDeletingRecordId(null);
      setRecordToDelete(null);
      // Defer toast so it shows after modal unmounts and is clearly visible
      setTimeout(() => {
        setToast({
          message: "Inventory record deleted successfully!",
          type: "success",
        });
      }, 0);
    } catch (error) {
      console.error("Failed to delete inventory record:", error);

      if (error.status === 409) {
        setToast({
          title: "Changes Not Saved",
          message:
            "This inventory record was updated by someone else. Please reload and try again.",
          type: "warning",
        });
        await refreshData();
        setShowDeleteRecordConfirmation(false);
        setDeletingRecordId(null);
        setRecordToDelete(null);
        return;
      }

      // Check if error is related to "no records" or "record not found"
      const isNoRecordsError =
        error.status === 404 ||
        (error.message &&
          (error.message.includes("No records") ||
            error.message.includes("no records") ||
            error.message.includes("Record not found") ||
            error.message.includes("record not found")));

      if (isNoRecordsError) {
        setToast({
          message: "No records present in the inventory",
          type: "info",
          hideTitle: true,
        });
      } else {
        let errorMessage =
          "Unable to process your request right now. Please try again later.";
        if (
          error.isNetworkError ||
          error.message === "Unable to load the data"
        ) {
          errorMessage = "Unable to load the data";
        } else if (error.status === 403) {
          errorMessage = "You do not have permission to delete this record";
        } else if (error.status !== 400 && error.status !== 500) {
          errorMessage = error.message || "Failed to delete inventory record";
        }
        setToast({
          message: errorMessage,
          type: "error",
        });
      }
      setShowDeleteRecordConfirmation(false);
      setDeletingRecordId(null);
      setRecordToDelete(null);
    } finally {
      setDeletingRecord(false);
    }
  };

  const handleCancelDeleteRecord = () => {
    setShowDeleteRecordConfirmation(false);
    setDeletingRecordId(null);
    setRecordToDelete(null);
  };

  const handleOpenSiteDetails = (payload) => {
    if (!payload) return;
    const { items: _items, ...addressParts } = payload;
    setSiteDetailsAddressKey(addressParts);
    setSiteDetailsSnapshotItems(isLiveView ? undefined : payload.items);
    setSiteDetailsOpen(true);
  };

  const handleViewInvoicesForAccount = useCallback(
    (billingAccount) => {
      const account = String(billingAccount || "").trim();
      if (!account || !onNavigate) return;
      try {
        sessionStorage.setItem(INVOICE_ACCOUNT_SEARCH_KEY, account);
      } catch {
        /* ignore */
      }
      onNavigate("invoices");
    },
    [onNavigate],
  );

  const handleSiteDetailsSaved = useCallback(
    (savedSite) => {
      if (!isLiveView || !siteDetailsAddressKey || !savedSite) return;
      patchGroupedSiteStatus(siteDetailsAddressKey, savedSite);
    },
    [isLiveView, siteDetailsAddressKey, patchGroupedSiteStatus],
  );

  // Helper function to check if any filters are active
  const hasActiveFilters = () => {
    return (
      (filters.provider &&
        ((Array.isArray(filters.provider) && filters.provider.length > 0) ||
          (!Array.isArray(filters.provider) && filters.provider))) ||
      (filters.category &&
        ((Array.isArray(filters.category) && filters.category.length > 0) ||
          (!Array.isArray(filters.category) && filters.category))) ||
      (filters.serviceType &&
        ((Array.isArray(filters.serviceType) &&
          filters.serviceType.length > 0) ||
          (!Array.isArray(filters.serviceType) && filters.serviceType))) ||
      (filters.billingAccount &&
        ((Array.isArray(filters.billingAccount) &&
          filters.billingAccount.length > 0) ||
          (!Array.isArray(filters.billingAccount) && filters.billingAccount)))
    );
  };

  const handleSave = async (updatedItem, changes) => {
    if (editingItem?.isSnapshotViewOnly) {
      return;
    }
    try {
      console.log("Saving inventory item:", updatedItem);
      console.log("Changes:", changes);

      /**
       * JSON Payload Structure for Inventory Update API
       *
       * This payload is sent to the backend API to update an inventory record.
       * Format: { fieldName: fieldValue, ... }
       *
       * Payload Rules:
       * 1. Only includes fields that were actually changed by the user
       * 2. Always includes 'edited_by' field for audit tracking
       * 3. Field names match database column names (not display names)
       * 4. Values are the new values from the form (not original values)
       * 5. Address fields (std_*) are excluded - they are updated via separate address API
       *
       * Example payload:
       * {
       *   "circuit_id": "ABC123",
       *   "status": "Active",
       *   "notes": "Updated by admin",
       *   "follow_up_date": "2024-12-31",
       *   "edited_by": "admin"
       * }
       */

      // Address fields are updated via standardizeAndSaveAddress API,
      // so they should be excluded from inventory update payload
      const addressFields = [
        "std_street_address",
        "std_city",
        "std_state",
        "std_zip",
        "std_country",
        "standard_address",
        "address_id",
        "address", // The concatenated address field
      ];

      // Provider and category are read-only fields and should never be changed
      const readOnlyFields = ["provider", "category"];

      // Combine all fields that should be excluded from update payload
      const excludedFields = [...addressFields, ...readOnlyFields];

      const updatePayload = {};

      // Include only the fields that were changed (excluding address fields and read-only fields)
      // Changes object format: { fieldName: { original, new, changed } }
      // When EditModal reports null/empty changes, only address was saved already —
      // do not re-POST every inventory field (that races the for-record version bump).
      if (changes && Object.keys(changes).length > 0) {
        Object.keys(changes).forEach((field) => {
          // Skip address fields and read-only fields - they should not be updated
          if (excludedFields.includes(field)) {
            return;
          }
          // Get the new value from the updatedItem
          if (updatedItem[field] !== undefined) {
            updatePayload[field] = updatedItem[field];
          }
        });
      }

      // Add edited_by field - can be enhanced to get from user context
      updatePayload.edited_by = "admin"; // TODO: Get from user context or auth
      updatePayload.version =
        updatedItem.version ?? editingItem?.version ?? null;

      console.log("Update payload:", updatePayload);

      // Check if we have any non-address fields to update
      // If payload only contains edited_by, it means only address fields were changed
      const hasNonAddressChanges =
        Object.keys(updatePayload).filter(
          (key) => key !== "edited_by" && key !== "version",
        ).length > 0;

      if (hasNonAddressChanges) {
        // Call the API to update the inventory record
        await updateInventoryRecord(updatedItem.id, updatePayload, tenantId);
        console.log("✅ Inventory record updated successfully");
      } else {
        // Only address fields were changed (already updated via standardizeAndSaveAddress)
        // Skip inventory update API call, but still refresh to show updated address
        console.log(
          "Only address fields changed - skipping inventory update API call",
        );
      }

      // Always refresh the data to show updated values (including address updates)
      await refreshData();

      // Refresh dropdown values to pick up any new providers/categories/statuses
      if (refreshDropdownValues) {
        refreshDropdownValues();
      }

      closeEditingItem();

      // Show success message
      setToast({
        message: "Inventory record updated successfully!",
        type: "success",
      });
    } catch (error) {
      console.error("Failed to update inventory record:", error);
      if (error.status === 409) {
        setToast({
          title: "Changes Not Saved",
          message: "This inventory record was updated by someone else. Please reload and try again.",
          type: "warning",
        });
        await refreshData();
        closeEditingItem();
        return;
      }
      if (error.status === 404) {
        setToast({
          title: "Changes Not Saved",
          message:
            "This inventory record was deleted by someone else. Please reload and try again.",
          type: "warning",
        });
        await refreshData();
        closeEditingItem();
        return;
      }
      // Show generic error message for 400, 500, and network errors
      let errorMessage =
        "Unable to process your request right now. Please try again later.";
      if (error.isNetworkError || error.message === "Unable to load the data") {
        errorMessage = "Unable to load the data";
      } else if (error.status !== 400 && error.status !== 500) {
        errorMessage = error.message || "Failed to update inventory record";
      }
      alert(`❌ ${errorMessage}`);
    }
  };

  // Step 1: Handle delete button click - get preview count
  const handleDeleteButtonClick = async () => {
    try {
      setToast({ message: null, type: "success" }); // Clear any existing messages
      setDeleting(true);

      // Get count of records that would be deleted
      // Include all active filters: provider, category, serviceType, and billingAccount
      const response = await getDeletePreviewCount({
        tenant_id: tenantId,
        provider: filters.provider,
        category: filters.category,
        service_type: filters.serviceType,
        billing_account: filters.billingAccount,
      });

      // Check if there are no records to delete
      if (response.count === 0) {
        setToast({
          message: "No records present in the inventory",
          type: "info",
          hideTitle: true,
        });
        return;
      }

      setDeletePreviewCount(response.count);
      setShowDeleteConfirmation(true);
    } catch (error) {
      console.error("Failed to get delete preview count:", error);

      // Check if error is related to "no records"
      const isNoRecordsError =
        error.status === 404 ||
        (error.message &&
          (error.message.includes("No records") ||
            error.message.includes("no records")));

      if (isNoRecordsError) {
        setToast({
          message: "No records present in the inventory",
          type: "info",
          hideTitle: true,
        });
      } else {
        let errorMessage =
          "Unable to process your request right now. Please try again later.";
        if (
          error.isNetworkError ||
          error.message === "Unable to load the data"
        ) {
          errorMessage = "Unable to load the data";
        } else if (error.status !== 400 && error.status !== 500) {
          errorMessage = error.message || "Failed to get delete preview";
        }
        setToast({
          message: errorMessage,
          type: "error",
        });
      }
    } finally {
      setDeleting(false);
    }
  };

  /**
   * @param {boolean} withDeleteSnapshot - true: save Azure delete snapshot (overwrite circuit); false: delete from DB only
   */
  const handleConfirmDeleteFiltered = async (withDeleteSnapshot) => {
    try {
      setDeleting(true);

      // Delete records matching all active filters: provider, category, serviceType, and billingAccount
      const response = await deleteFilteredInventory({
        tenant_id: tenantId,
        provider: filters.provider,
        category: filters.category,
        service_type: filters.serviceType,
        billing_account: filters.billingAccount,
        skip_delete_snapshot: !withDeleteSnapshot,
        preserve_existing_delete_snapshot: false,
      });

      // Check if no records were deleted
      if (response.deleted_count === 0) {
        setShowDeleteConfirmation(false);
        setToast({
          message: "No records present in the inventory",
          type: "info",
          hideTitle: true,
        });
        return;
      }

      // Refresh data after deletion
      await refreshData();

      // Refresh dropdown values to reflect changes
      if (refreshDropdownValues) {
        refreshDropdownValues();
      }

      setShowDeleteConfirmation(false);
      setToast({
        message: `Successfully deleted ${deletePreviewCount} inventory record(s)`,
        type: "success",
      });
    } catch (error) {
      console.error("Failed to delete inventory records:", error);

      // Check if error is related to "no records"
      const isNoRecordsError =
        error.status === 404 ||
        (error.message &&
          (error.message.includes("No records") ||
            error.message.includes("no records")));

      if (isNoRecordsError) {
        setToast({
          message: "No records present in the inventory",
          type: "info",
          hideTitle: true,
        });
      } else {
        let errorMessage =
          "Unable to process your request right now. Please try again later.";
        if (
          error.isNetworkError ||
          error.message === "Unable to load the data"
        ) {
          errorMessage = "Unable to load the data";
        } else if (error.status !== 400 && error.status !== 500) {
          errorMessage = error.message || "Failed to delete records";
        }
        setToast({
          message: errorMessage,
          type: "error",
        });
      }
      setShowDeleteConfirmation(false);
    } finally {
      setDeleting(false);
    }
  };

  // Keep the old truncate function for backward compatibility (if needed elsewhere)
  const handleTruncateTables = async () => {
    const confirmed = window.confirm(
      "This will delete all inventory data. This action cannot be undone. Are you sure you want to continue?",
    );

    if (!confirmed) {
      return;
    }

    try {
      setTruncating(true);
      await truncateTables();
      await refreshData();

      // Refresh dropdown values after truncate (since all data is deleted)
      if (refreshDropdownValues) {
        refreshDropdownValues();
      }

      setToast({
        message: "Inventory table data deleted successfully!",
        type: "success",
      });
    } catch (error) {
      console.error("Failed to truncate tables:", error);
      let errorMessage =
        "Unable to process your request right now. Please try again later.";
      if (error.isNetworkError || error.message === "Unable to load the data") {
        errorMessage = "Unable to load the data";
      } else if (error.status !== 400 && error.status !== 500) {
        errorMessage = error.message || "Failed to truncate tables";
      }
      alert(`${errorMessage}`);
    } finally {
      setTruncating(false);
    }
  };

  const handleExportRecords = async () => {
    try {
      setToast({ message: null, type: "success" }); // Clear any existing messages
      setExporting(true);
      await exportInventoryRecords({
        tenant_id: tenantId,
        provider: filters.provider || undefined,
        category: filters.category || undefined,
        search: filters.search || undefined,
        sortBy: sortConfig.sortBy || undefined,
        sortOrder: sortConfig.sortOrder || undefined,
      });
      setToast({
        message: "Successfully exported inventory records!",
        type: "success",
      });
    } catch (error) {
      console.error("Failed to export records:", error);

      // Check if error is related to "no records"
      const isNoRecordsError =
        error.status === 404 ||
        (error.message &&
          (error.message.includes("No records found to export") ||
            error.message.includes(
              "No inventory records match the specified filters",
            ) ||
            error.message.includes(
              "Downloaded file is empty. No records to export.",
            )));

      if (isNoRecordsError) {
        setToast({
          message: "No inventory records available to export",
          type: "info",
          hideTitle: true,
        });
      } else {
        let errorMessage = "Unable to export records. Please try again later.";
        if (
          error.isNetworkError ||
          error.message === "Unable to load the data"
        ) {
          errorMessage = "Unable to load the data";
        } else if (error.status !== 400 && error.status !== 500) {
          errorMessage = error.message || "Failed to export records";
        }
        setToast({
          message: errorMessage,
          type: "error",
        });
      }
    } finally {
      setExporting(false);
    }
  };

  // Transform grouped inventory data - data is already grouped from backend
  // Add formatted address to each group
  const transformedInventoryData = useMemo(() => {
    if (!inventoryData || inventoryData.length === 0) {
      return inventoryData;
    }
    // inventoryData is already an array of address groups from backend
    return inventoryData.map((group) => ({
      ...group,
      address: formatConcatenatedAddress({
        std_street_address: group.std_street_address,
        std_city: group.std_city,
        std_state: group.std_state,
        std_zip: group.std_zip,
        std_country: group.std_country,
      }),
    }));
  }, [inventoryData]);

  // Use billing account provider map from dropdown values (from backend)
  // This is the primary source - it comes from the backend API with all billing accounts
  // Fallback to building from current page data if needed
  const currentPageBillingAccountProviderMap = useMemo(() => {
    return buildBillingAccountProviderMap(filterBarInventory);
  }, [filterBarInventory]);

  // Merge backend mapping (from dropdown API) with current page mapping
  // Backend mapping takes precedence as it has all records
  const billingAccountProviderMap = useMemo(() => {
    const merged = new Map();

    // First, add all from backend mapping (most complete)
    if (billingAccountProviderMapFromBackend) {
      billingAccountProviderMapFromBackend.forEach((provider, account) => {
        merged.set(account, provider);
      });
    }

    // Then add any from current page that might not be in backend map (fallback)
    currentPageBillingAccountProviderMap.forEach((provider, account) => {
      if (!merged.has(account)) {
        merged.set(account, provider);
      }
    });

    return merged;
  }, [
    billingAccountProviderMapFromBackend,
    currentPageBillingAccountProviderMap,
  ]);

  const displayLoading = isLiveView ? loading : snapshotLoading;
  const displayError = isLiveView
    ? error
    : snapshotReportError
      ? snapshotReportError.message || "Unable to load snapshot"
      : null;
  const hasSnapshotSourceRows =
    Array.isArray(snapshotRowsRaw) && snapshotRowsRaw.length > 0;
  const showSnapshotTable =
    !isLiveView &&
    !snapshotLoading &&
    !snapshotReportError &&
    hasSnapshotSourceRows &&
    transformedSnapshotData.length > 0;
  const showSnapshotNoDataMessage =
    !isLiveView &&
    !snapshotLoading &&
    !snapshotReportError &&
    !hasSnapshotSourceRows;
  const showSnapshotFilteredEmpty =
    !isLiveView &&
    !snapshotLoading &&
    !snapshotReportError &&
    hasSnapshotSourceRows &&
    transformedSnapshotData.length === 0;

  const headerLoading = isLiveView ? loading : snapshotLoading;
  const headerGrandTotal = isLiveView ? grandTotalMRC : snapshotGrandTotalMRC;
  // Sites = configured sites only (not every address row).
  // Address list total stays separate for "Showing X of Y addresses".
  const headerSiteCount = isLiveView
    ? pagination.siteCount != null
      ? pagination.siteCount
      : 0
    : snapshotPagination.siteCount != null
      ? snapshotPagination.siteCount
      : 0;
  const headerAddressCount = isLiveView
    ? pagination.total
    : snapshotPagination.totalGroups;
  const headerServiceCount = isLiveView
    ? pagination.totalRecords
    : snapshotPagination.totalRecords;
  const allowDelete = canMutateTenantData(userRole);

  return (
    <div className="min-h-screen pt-[80px] bg-[var(--ground,#f5f4f1)]">
      {/* Toast Notification - z-[100] so it appears above delete/loading modals (z-50) */}
      <div className="fixed top-4 right-4 z-[100]">
        <Toast
          message={toast.message}
          title={toast.title}
          type={toast.type}
          hideTitle={toast.hideTitle || false}
          onClose={() => setToast({ message: null, type: "success" })}
        />
      </div>

      <div className="w-full px-5 py-2 pb-14">
        <div className="mb-5 flex flex-col xl:flex-row xl:items-end xl:justify-between gap-4">
          <div>
            <p className="text-[12px] font-semibold uppercase tracking-[0.05em] text-[var(--accent,#a51c1c)]">
              Inventory
            </p>
            <h1 className="mt-1 text-[26px] font-semibold leading-tight text-[var(--ink,#1c1c1a)]">
              Detailed Inventory
            </h1>
          </div>
          <div className="flex flex-wrap items-end gap-2.5 shrink-0">
            <div className="inline-flex items-stretch overflow-hidden rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white shadow-sm">
              <div className="px-3 py-2 border-r border-[var(--line,#e4e2dc)]">
                <label className="block text-[10px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">
                  Billing period
                </label>
                <div className="mt-0.5 h-7 inline-flex items-center text-[13px] font-medium text-[var(--ink,#1c1c1a)]">
                  {currentBillingPeriod}
                </div>
              </div>
              <div className="px-3 py-2 border-r border-[var(--line,#e4e2dc)]">
                <label className="block text-[10px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">
                  Location
                </label>
                <div className="mt-0.5 h-7 inline-flex items-center text-[13px] font-semibold tabular-nums text-[var(--ink,#1c1c1a)]">
                  {headerLoading
                    ? "…"
                    : Number(headerAddressCount || 0).toLocaleString()}
                </div>
              </div>
              <div className="px-3 py-2 border-r border-[var(--line,#e4e2dc)]">
                <label className="block text-[10px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">
                  Sites
                </label>
                <div className="mt-0.5 h-7 inline-flex items-center text-[13px] font-semibold tabular-nums text-[var(--ink,#1c1c1a)]">
                  {headerLoading ? "…" : Number(headerSiteCount || 0).toLocaleString()}
                </div>
              </div>
              <div className="px-3 py-2 border-r border-[var(--line,#e4e2dc)]">
                <label className="block text-[10px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">
                  Services
                </label>
                <div className="mt-0.5 h-7 inline-flex items-center text-[13px] font-semibold tabular-nums text-[var(--ink,#1c1c1a)]">
                  {headerLoading
                    ? "…"
                    : Number(headerServiceCount || 0).toLocaleString()}
                </div>
              </div>
              <div className="px-3 py-2">
                <label className="block text-[10px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">
                  Grand total MRC
                </label>
                <div className="mt-0.5 h-7 inline-flex items-center text-[13px] font-semibold tabular-nums text-[var(--ink,#1c1c1a)]">
                  {headerLoading ? "…" : formatCurrency(headerGrandTotal || 0)}
                </div>
              </div>
            </div>
            {isLiveView ? (
              <button
                type="button"
                onClick={handleExportRecords}
                disabled={exporting || headerLoading}
                className="inline-flex h-[52px] items-center gap-2 rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white px-3.5 text-[13px] font-medium text-[var(--ink,#1c1c1a)] shadow-sm hover:bg-[var(--sunken,#eeece7)] disabled:opacity-50"
              >
                <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path
                    fillRule="evenodd"
                    d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
                {exporting ? "Exporting..." : "Export"}
              </button>
            ) : null}
            {isLiveView && allowDelete ? (
              <button
                type="button"
                onClick={handleDeleteButtonClick}
                disabled={deleting || headerLoading}
                className="inline-flex h-[52px] items-center gap-2 rounded-[10px] border border-[var(--accent,#a51c1c)] bg-white px-3.5 text-[13px] font-medium text-[var(--accent,#a51c1c)] shadow-sm hover:bg-[var(--accent-soft,#f3e8e8)] disabled:opacity-50"
              >
                {deleting ? "Deleting..." : "Delete"}
              </button>
            ) : null}
          </div>
        </div>
        <FilterBar
          filters={filters}
          onFilterChange={updateFilters}
          inventory={filterBarInventory}
          providers={filterBarProviders ?? providers}
          categories={categories}
          serviceTypes={serviceTypes}
          billingAccounts={filterBarBillingAccounts ?? billingAccounts}
          billingAccountProviderMap={
            filterBarBillingAccountProviderMap ?? billingAccountProviderMapFromBackend
          }
          filterCombinations={filterCombinations}
          billingAccountsByProvider={
            filterBarBillingAccountsByProvider ?? billingAccountsByProvider
          }
          categoriesByProvider={categoriesByProvider}
          serviceTypesByProvider={serviceTypesByProvider}
          siteFilterOptions={siteFilterOptions}
          siteFilterOptionsLoading={siteFilterOptionsLoading}
          shownCount={
            isLiveView
              ? transformedInventoryData.length
              : transformedSnapshotData.length
          }
          totalCount={headerAddressCount}
          sortConfig={isLiveView ? sortConfig : snapshotSortConfig}
        />

        <div className="mt-3 mb-6">
          {displayLoading && (
            <div className="p-6 flex flex-col items-center justify-center text-gray-500">
              <SpinnerCustom />
            </div>
          )}

          {!displayLoading && displayError && (
            <div className="p-1 text-center text-red-500 text-xs">
              {typeof displayError === "string" &&
              (displayError === "Unable to load the data" ||
                displayError.includes("Unable to load the data") ||
                displayError.includes("Failed to fetch"))
                ? "Unable to load the data"
                : `Error: ${displayError}`}
            </div>
          )}

          {!displayLoading &&
            !displayError &&
            isLiveView &&
            inventoryData.length > 0 && (
              <GroupedInventoryTable
                providers={providers}
                categories={categories}
                inventory={transformedInventoryData}
                userRole={userRole}
                tenant={tenant}
                tenantId={tenantId}
                onEditItem={handleEditItem}
                onViewInvoices={handleViewInvoicesForAccount}
                onDeleteItem={
                  canMutateTenantData(userRole) ? handleDeleteItem : undefined
                }
                onOpenSiteDetails={handleOpenSiteDetails}
                currentPage={pagination.page}
                totalPages={pagination.totalPages}
                totalRecords={pagination.totalRecords}
                totalGroups={pagination.total}
                pageSize={pagination.pageSize}
                onPageChange={changePage}
                onPageSizeChange={changePageSize}
                sortConfig={sortConfig}
                onSortChange={updateSort}
                columnMapping={columnMapping}
                fieldMappingLookup={fieldMappingLookup}
              />
            )}

          {!displayLoading && !displayError && showSnapshotTable && (
            <GroupedInventoryTable
              providers={providers}
              categories={categories}
              inventory={transformedSnapshotData}
              userRole={userRole}
              tenant={tenant}
              tenantId={tenantId}
              onEditItem={handleEditItem}
              onViewInvoices={handleViewInvoicesForAccount}
              onDeleteItem={undefined}
              onOpenSiteDetails={handleOpenSiteDetails}
              currentPage={snapshotPagination.page}
              totalPages={snapshotPagination.totalPages}
              totalRecords={snapshotPagination.totalRecords}
              totalGroups={snapshotPagination.totalGroups}
              pageSize={snapshotPagination.pageSize}
              onPageChange={changeSnapshotPage}
              onPageSizeChange={changeSnapshotPageSize}
              sortConfig={snapshotSortConfig}
              onSortChange={handleSnapshotSort}
              columnMapping={columnMapping}
              fieldMappingLookup={fieldMappingLookup}
            />
          )}

          {!displayLoading &&
            !displayError &&
            isLiveView &&
            inventoryData.length === 0 && (
              <div className="p-1 text-center text-gray-500 text-xs">
                No inventory records found.
              </div>
            )}

          {!displayLoading && !displayError && showSnapshotNoDataMessage && (
            <div className="p-6 text-center text-gray-600 text-sm">
              No monthly inventory snapshot is available for this month. Snapshots
              are created when the monthly snapshot job runs; use{" "}
              <strong>Current</strong> to view live inventory.
            </div>
          )}

          {!displayLoading && !displayError && showSnapshotFilteredEmpty && (
            <div className="p-6 text-center text-gray-600 text-sm">
              No snapshot records match the current filters. Try clearing filters
              or pick a different month.
            </div>
          )}
        </div>
      </div>

      <SiteDetailsModal
        open={siteDetailsOpen}
        onClose={() => {
          setSiteDetailsOpen(false);
          setSiteDetailsAddressKey(null);
          setSiteDetailsSnapshotItems(undefined);
          // Clear any existing toast
          setToast({ title: "", message: "", type: "success" });
        }}
        addressKey={siteDetailsAddressKey || undefined}
        snapshotGroupItems={siteDetailsSnapshotItems}
        tenant={tenant}
        tenantId={tenantId}
        userRole={userRole}
        onSaved={handleSiteDetailsSaved}
        allowCreate={canMutateTenantData(userRole) && isLiveView}
      />

      {editingItem && (
        <EditModal
          item={editingItem.current}
          originalItem={editingItem.original}
          onClose={closeEditingItem}
          onSave={handleSave}
          tenant={tenant}
          tenantId={tenantId}
          useAddressForRecordOnly
          isNewRecord={editingItem.isNew}
          provider={editingItem.current?.provider || filters.provider || ""}
          providers={providers}
          categories={categories}
          serviceTypes={serviceTypes}
          category={editingItem.current?.category || filters.category || ""}
          fieldMapping={columnMapping || {}}
          apiColumns={columns || []}
          fieldMappingLookup={fieldMappingLookup || {}}
          canMutate={
            editingItem.isSnapshotViewOnly
              ? false
              : canMutateTenantData(userRole)
          }
          viewContext={
            editingItem.isSnapshotViewOnly ? "snapshot" : null
          }
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-4 max-w-xl w-full mx-4 shadow-xl">
            <h3 className="text-base font-bold text-gray-900 mb-3">
              Confirm Delete
            </h3>

            {/* Show active filters or "All Records" warning */}
            {hasActiveFilters() ? (
              <div className="mb-4">
                <p className="text-xs text-gray-700 mb-2">
                  You are about to delete{" "}
                  <strong className="text-gray-900">
                    {deletePreviewCount}
                  </strong>{" "}
                  record(s) matching these filters:
                </p>
                <div className="bg-gray-50 p-2 rounded border border-gray-200 space-y-1.5">
                  {filters.provider &&
                    ((Array.isArray(filters.provider) &&
                      filters.provider.length > 0) ||
                      !Array.isArray(filters.provider)) && (
                      <div className="text-xs">
                        <span className="font-semibold text-gray-700">
                          Provider:{" "}
                        </span>
                        <span className="text-gray-600">
                          {Array.isArray(filters.provider)
                            ? filters.provider.join(", ")
                            : filters.provider}
                        </span>
                      </div>
                    )}
                  {filters.category &&
                    ((Array.isArray(filters.category) &&
                      filters.category.length > 0) ||
                      !Array.isArray(filters.category)) && (
                      <div className="text-xs">
                        <span className="font-semibold text-gray-700">
                          Category:{" "}
                        </span>
                        <span className="text-gray-600">
                          {Array.isArray(filters.category)
                            ? filters.category.join(", ")
                            : filters.category}
                        </span>
                      </div>
                    )}
                  {filters.serviceType &&
                    ((Array.isArray(filters.serviceType) &&
                      filters.serviceType.length > 0) ||
                      !Array.isArray(filters.serviceType)) && (
                      <div className="text-xs">
                        <span className="font-semibold text-gray-700">
                          Service Type:{" "}
                        </span>
                        <span className="text-gray-600">
                          {Array.isArray(filters.serviceType)
                            ? filters.serviceType.join(", ")
                            : filters.serviceType}
                        </span>
                      </div>
                    )}
                  {filters.billingAccount &&
                    ((Array.isArray(filters.billingAccount) &&
                      filters.billingAccount.length > 0) ||
                      !Array.isArray(filters.billingAccount)) && (
                      <div className="text-xs">
                        <span className="font-semibold text-gray-700">
                          Billing Account:{" "}
                        </span>
                        <span className="text-gray-600">
                          {Array.isArray(filters.billingAccount)
                            ? filters.billingAccount.join(", ")
                            : filters.billingAccount}
                        </span>
                      </div>
                    )}
                </div>
              </div>
            ) : (
              <div className="mb-4 p-3 bg-gray-50 border border-gray-300 rounded">
                <p className="text-xs text-gray-800 font-semibold mb-1.5">
                  WARNING: No filters selected!
                </p>
                <p className="text-xs text-gray-700">
                  This will delete ALL{" "}
                  <strong className="font-bold">{deletePreviewCount}</strong>{" "}
                  inventory records in the system.
                </p>
              </div>
            )}

            <p className="mb-2 text-[11px] text-gray-600">
            This action cannot be undone if you choose permanent deletion. Please select how you want to proceed.
            </p>

            <div className="flex flex-wrap items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowDeleteConfirmation(false)}
                disabled={deleting}
                className="inline-flex items-center justify-center px-3 py-1.5 border border-gray-300 text-[15px] font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleConfirmDeleteFiltered(false)}
                disabled={deleting}
                className="inline-flex items-center justify-center px-3 py-1.5 border-0 text-[15px] font-medium rounded-md text-white bg-[var(--accent,#a51c1c)] hover:bg-[#8c1a1a] focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {deleting ? "Deleting..." : "Delete Permanently"}
              </button>
              {/* <button
                type="button"
                onClick={() => handleConfirmDeleteFiltered(true)}
                disabled={deleting}
                className="inline-flex items-center justify-center px-3 py-1.5 border-0 text-[15px] font-medium rounded-md text-white bg-brand-red hover:bg-red-600 focus:outline-none disabled:bg-gray-400 disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
              >
                {deleting ? "Deleting..." : "Delete & Preserve for Future Import"}
              </button> */}
            </div>
          </div>
        </div>
      )}

      <Loading
        show={deleting || deletingRecord || exporting}
        overlayMode="fixed"
        message={
          exporting
            ? "Exporting inventory…"
            : deleting || deletingRecord
              ? "Deleting records…"
              : "Processing…"
        }
        subMessage="Please wait. This may take a moment."
      />

      {/* Individual Record Delete Confirmation Modal */}
      {showDeleteRecordConfirmation && recordToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-4 max-w-xl w-full mx-4 shadow-xl">
            <h3 className="text-base font-bold text-gray-900 mb-3">
              Confirm Delete
            </h3>

            <div className="mb-4">
              <p className="text-xs text-gray-700 mb-2">
                Are you sure you want to delete this inventory record?
              </p>
              <div className="bg-gray-50 p-2 rounded border border-gray-200 space-y-1.5">
                {recordToDelete.circuit_id && (
                  <div className="text-xs">
                    <span className="font-semibold text-gray-700">
                      Circuit ID:{" "}
                    </span>
                    <span className="text-gray-600">
                      {recordToDelete.circuit_id}
                    </span>
                  </div>
                )}
                {recordToDelete.service_type && (
                  <div className="text-xs">
                    <span className="font-semibold text-gray-700">
                      Service Type:{" "}
                    </span>
                    <span className="text-gray-600">
                      {recordToDelete.service_type}
                    </span>
                  </div>
                )}
                {!recordToDelete.circuit_id && !recordToDelete.service_type && (
                  <div className="text-xs">
                    <span className="font-semibold text-gray-700">
                      Record ID:{" "}
                    </span>
                    <span className="text-gray-600">{recordToDelete.id}</span>
                  </div>
                )}
              </div>
            </div>

            <p className="mb-2 text-[11px] text-gray-600">
            This action cannot be undone if you choose permanent deletion.Please select how you want to proceed.
            </p>

            <div className="flex flex-wrap items-center justify-end gap-2">
              <button
                type="button"
                onClick={handleCancelDeleteRecord}
                disabled={deletingRecord}
                className="inline-flex items-center justify-center px-3 py-1.5 border border-gray-300 text-[15px] font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleConfirmDeleteRecord(false)}
                disabled={deletingRecord}
                className="inline-flex items-center justify-center px-3 py-1.5 border-0 text-[15px] font-medium rounded-md text-white bg-[var(--accent,#a51c1c)] hover:bg-[#8c1a1a] focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {deletingRecord ? "Deleting..." : "Delete Permanently"}
              </button>
              {/* <button
                type="button"
                onClick={() => handleConfirmDeleteRecord(true)}
                disabled={deletingRecord}
                className="inline-flex items-center justify-center px-3 py-1.5 border-0 text-[15px] font-medium rounded-md text-white bg-brand-red hover:bg-red-600 focus:outline-none disabled:bg-gray-400 disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
              >
                {deletingRecord ? "Deleting..." : "Delete & Preserve for Future Import"}
              </button> */}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryPage;
